import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../shared/controls/pageable-table/pageable-table.component';

import { DialogService } from '../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { InitializerService } from '../../../shared/services/initializer-service';
import { ErrorService } from '../../../shared/services/error-service';
import { AuthService } from '../../../shared/services/auth.service';

import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../../shared/constants/area.constants';
import { PermissionConstants } from '../../../shared/constants/permission.constants';

import { ClientService, ClientAreaService, ClientAreaByClientIdItem, PagingData } from '../../../../service/proxy';

@Component({
    selector: 'client-area',
    templateUrl: './client-area.component.html',
    styleUrls: ['./client-area.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        HasPermissionDirective,
    ],

    providers: [
        ClientService,
        ClientAreaService,
    ]
})
export class ClientAreaComponent implements OnInit {
    @Input()
    clientId: number = 0;

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public clientAreas: ClientAreaByClientIdItem[] = [];

    constructor(
        public authService: AuthService,
        private router: Router,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private clientService: ClientService,
        private clientAreaService: ClientAreaService)
    {
        this.paging = this.initializerService.createPagingData();
        this.paging.sortColumn = 'areaName';
        this.paging.sortDirection = 'ASC'; 
    }

    ngOnInit(): void {
        this.initializePermissions();
    }

    initializePermissions(): void {
        this.showEdit = this.authService
            .hasPermission(AreaConstants.Client, PermissionConstants.Edit);

        this.showDelete = this.authService
            .hasPermission(AreaConstants.Client, PermissionConstants.Delete);
    }

    getClientAreasByClientId(pagingData: PagingData = new PagingData): void {
        pagingData.sortColumn = pagingData.sortColumn || 'areaName';
        pagingData.sortDirection = pagingData.sortDirection || 'ASC';

        this.clientService.getClientAreasByClientId(this.clientId, this.search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.clientAreas = (data.results || []).map(item => {
                        if (item.inceptionDate) {
                            item.inceptionDate = formatDate(item.inceptionDate, 'yyyy/MM/dd', 'en-US') as any;
                        }
                        else {
                            item.inceptionDate = '';
                        }

                        return item;
                    });
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.clientAreas = [];
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    getColumnMap() {
        return {
            areaName: 'Policy Name',
            policyNumber: 'Policy Number',
            inceptionDate: 'Inception Date',
            insurerName: 'Insurer',
            description: 'Industry',
            policyBase: 'Policy Base',
            policyGroup: 'Policy Group',
            ageingIncludesVat: 'Ageing Includes VAT',
            salesIncludesVat: 'Sales Includes VAT',
        };
    }

    addClientAreas(): void {
        this.router.navigate([`/client-area/${this.clientId}/0`]);
    }

    editClientArea(clientAreaId: number): void {
        this.router.navigate([`/client-area/${this.clientId}/${clientAreaId}`]);
    }

    deleteClientArea(clientAreaId: number): void {
        this.dialogService.show('Delete Policy', 'Are you sure you want to delete this policy?', 'warning').subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.clientAreaService.deleteClientArea(clientAreaId).subscribe({
                    next: () => {
                        this.toastService.show('Policy deleted successfully.', 'success');
                        this.getClientAreasByClientId(this.paging);
                    },
                    error: () => {
                        this.toastService.show('Failed to delete policy.', 'error');
                    }
                });
            }
        });
    }
}
